import { Ionicons } from '@expo/vector-icons';
import { Redirect, router } from 'expo-router';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import React, { useState } from 'react';
import { View } from 'react-native';

import { ChargeDetailModal } from '@/components/enrollments/charge-detail-modal';
import { getFirstName, MemberAvatar } from '@/components/team/member-avatar';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { FadeInView } from '@/components/ui/fade-in-view';
import { FilterBar } from '@/components/ui/filter-chips';
import { PageLayout } from '@/components/ui/page-layout';
import { Pagination } from '@/components/ui/pagination';
import { PressableScale } from '@/components/ui/pressable-scale';
import { SkeletonListRow } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { CHARGE_AUTOMATIC_BADGE } from '@/content/payments/charge';
import { useCharge } from '@/hooks/use-charges';
import { useConfirmPayment } from '@/hooks/use-confirm-payment';
import { useMembership } from '@/hooks/use-membership';
import { useTeamPayments } from '@/hooks/use-team-payments';
import { useTerms } from '@/hooks/use-terms';
import { useToastStore } from '@/store/toastStore';
import { CHARGE_STATUS } from '@/types/charge-status';
import { TeamCharge, TeamPaymentsFilter } from '@/types/charge';
import { PROCESSING_METHOD } from '@/types/processing-method';
import { getApiErrorMessage } from '@/utils/api-error';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format-currency';

const ALL = 'all';

const EMPTY_MESSAGE: Record<TeamPaymentsFilter, string> = {
  OPEN: 'Nenhuma cobrança em aberto na equipe.',
  PAYMENT_REPORTED: 'Ninguém informou pagamento por enquanto.',
  PENDING: 'Nada a vencer por enquanto.',
  OVERDUE: 'Nenhuma cobrança vencida.',
};

/**
 * Pagamentos da equipe no caixa único: o dono (ou a gerência) vê as cobranças
 * de todos os profissionais, com "aguardando confirmação" no topo, e dá baixa
 * em sequência sem trocar o foco. Cobrança automática (`processingMethod ===
 * PLATFORM`, subconta do dono, T7.2) ganha a etiqueta "Automático" e nunca
 * mostra "Confirmar pagamento" — o Stripe confirma sozinho pelo webhook.
 */
export default function TeamPaymentsScreen() {
  const { membership, can } = useMembership();
  const terms = useTerms();
  const { addToast } = useToastStore();
  const confirmPayment = useConfirmPayment();

  const [status, setStatus] = useState<TeamPaymentsFilter>('OPEN');
  const [providerId, setProviderId] = useState<string>(ALL);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [selectedChargeId, setSelectedChargeId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useTeamPayments({
    status,
    providerId: providerId === ALL ? undefined : providerId,
    page,
    limit,
  });
  const { data: selectedCharge } = useCharge(selectedChargeId || undefined);

  if (membership && !can('teamPayments')) {
    return <Redirect href={'/(tabs)/(provider)/charges' as never} />;
  }

  const back = () =>
    router.canGoBack() ? router.back() : router.replace('/(tabs)/(provider)/charges' as never);

  const counts = data?.counts;
  const withCount = (label: string, n?: number) => (n ? `${label} · ${n}` : label);
  const statusOptions: { value: TeamPaymentsFilter; label: string }[] = [
    { value: 'OPEN', label: 'Em aberto' },
    { value: 'PAYMENT_REPORTED', label: withCount('Aguardando', counts?.reported.count) },
    { value: 'PENDING', label: 'A vencer' },
    { value: 'OVERDUE', label: withCount('Vencidas', counts?.overdue.count) },
  ];
  const memberOptions = [
    { value: ALL, label: 'Todos' },
    ...(data?.members ?? []).map((m) => ({ value: m.providerId, label: getFirstName(m.name) })),
  ];

  const charges = data?.data ?? [];
  const total = data?.meta.total ?? 0;

  const onConfirm = async (charge: TeamCharge) => {
    setConfirmingId(charge.id);
    try {
      await confirmPayment.mutateAsync(charge.id);
      addToast(`Pagamento de ${charge.clientName ?? terms.client} confirmado.`, 'success');
    } catch (err) {
      addToast(getApiErrorMessage(err, 'Não foi possível confirmar o pagamento.'), 'error');
    } finally {
      setConfirmingId(null);
    }
  };

  return (
    <>
      <PageLayout
        title="Pagamentos da equipe"
        subtitle="Confira o que os clientes pagaram e dê baixa"
        onBack={back}
      >
        {counts && (
          <FadeInView delay={0}>
            <View
              className="w-full flex-row gap-3 rounded-2xl bg-ink p-3"
              style={{ borderCurve: 'continuous' }}
            >
              <SummaryTile label="Aguardando" bucket={counts.reported} />
              <SummaryTile label="A vencer" bucket={counts.pending} />
              <SummaryTile label="Vencidas" bucket={counts.overdue} />
            </View>
          </FadeInView>
        )}

        <View className="gap-3">
          <FilterBar
            label="Situação"
            options={statusOptions}
            value={status}
            columns={2}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
          />
          {memberOptions.length > 2 && (
            <FilterBar
              label={terms.title.professional}
              options={memberOptions}
              value={providerId}
              onChange={(value) => {
                setProviderId(value);
                setPage(1);
              }}
            />
          )}
        </View>

        <Card className="w-full">
          <CardHeader>
            <CardTitle>
              {statusOptions.find((o) => o.value === status)?.label.split(' · ')[0]}
            </CardTitle>
            {total > 0 && (
              <ThemedText className="text-xs text-neutral-500">
                {total} {total === 1 ? 'cobrança' : 'cobranças'}
              </ThemedText>
            )}
          </CardHeader>

          <CardContent className="w-full pt-0">
            {isLoading ? (
              <View className="py-2">
                {Array.from({ length: 5 }).map((_, index) => (
                  <SkeletonListRow key={index} />
                ))}
              </View>
            ) : error ? (
              <ErrorState
                variant="inline"
                title="Erro ao carregar pagamentos"
                message={getApiErrorMessage(error, 'Tente de novo em instantes.')}
                onRetry={refetch}
              />
            ) : charges.length === 0 ? (
              <EmptyState
                icon="checkmark-done-outline"
                title="Tudo em dia"
                description={EMPTY_MESSAGE[status]}
                className="py-10"
              />
            ) : (
              <View className="w-full">
                {charges.map((charge, index) => (
                  <FadeInView key={charge.id} delay={index * 30}>
                    <ChargeRow
                      charge={charge}
                      member={data?.members.find((m) => m.providerId === charge.memberProviderId)}
                      last={index === charges.length - 1}
                      confirming={confirmingId === charge.id}
                      confirmDisabled={!!confirmingId}
                      onOpen={() => setSelectedChargeId(charge.id)}
                      onConfirm={() => void onConfirm(charge)}
                    />
                  </FadeInView>
                ))}
              </View>
            )}
          </CardContent>
        </Card>

        {total > 0 && !isLoading && (
          <Pagination
            currentPage={page}
            totalPages={data?.meta.totalPages || 1}
            onPageChange={setPage}
            itemsPerPage={limit}
            onItemsPerPageChange={(n) => {
              setLimit(n);
              setPage(1);
            }}
            itemsPerPageOptions={[20, 50, 100]}
          />
        )}
      </PageLayout>

      <ChargeDetailModal
        visible={!!selectedChargeId}
        charge={selectedCharge || null}
        onClose={() => setSelectedChargeId(null)}
        onUpdate={() => {
          refetch();
          setSelectedChargeId(null);
        }}
      />
    </>
  );
}

function SummaryTile({
  label,
  bucket,
}: {
  label: string;
  bucket: { count: number; amount: number };
}) {
  return (
    <View className="flex-1 gap-1 rounded-2xl bg-white/10 p-3">
      <ThemedText numberOfLines={1} className="font-medium text-xs text-white/60">
        {`${label} (${bucket.count})`}
      </ThemedText>
      <ThemedText
        className="font-semibold text-base text-white"
        style={{ fontVariant: ['tabular-nums'] }}
        numberOfLines={1}
        adjustsFontSizeToFit
      >
        {formatCurrency(bucket.amount)}
      </ThemedText>
    </View>
  );
}

function ChargeRow({
  charge,
  member,
  last,
  confirming,
  confirmDisabled,
  onOpen,
  onConfirm,
}: {
  charge: TeamCharge;
  member?: { name: string; image: string | null; color: string | null };
  last: boolean;
  confirming: boolean;
  confirmDisabled: boolean;
  onOpen: () => void;
  onConfirm: () => void;
}) {
  const reported = charge.status === CHARGE_STATUS.PAYMENT_REPORTED;
  const isAutomatic = charge.processingMethod === PROCESSING_METHOD.PLATFORM;
  const memberName = charge.memberName ?? member?.name ?? '';
  const serviceLabel = charge.description || charge.serviceName;
  const dueLabel = `Vence ${format(new Date(charge.dueDate), 'dd/MM/yy', { locale: ptBR })}`;

  return (
    <View className={cn('gap-2 py-3.5', !last && 'border-b border-neutral-100')}>
      <PressableScale
        haptic
        accessibilityRole="button"
        onPress={onOpen}
        className="flex-row items-center gap-3"
      >
        <MemberAvatar name={memberName} image={member?.image} color={member?.color} size={40} />

        <View className="min-w-0 flex-1 gap-0.5">
          <View className="flex-row flex-wrap items-center gap-1.5">
            <ThemedText
              className="font-semibold text-sm leading-tight text-foreground"
              numberOfLines={1}
            >
              {charge.clientName || (charge.isManual ? 'Cobrança manual' : 'Cobrança')}
            </ThemedText>
            {isAutomatic && (
              <View className="rounded-full bg-primary/10 px-2 py-0.5">
                <ThemedText className="font-semibold text-[11px] text-primary">
                  {CHARGE_AUTOMATIC_BADGE}
                </ThemedText>
              </View>
            )}
          </View>
          <ThemedText className="text-xs text-neutral-500" numberOfLines={1}>
            {[getFirstName(memberName), serviceLabel].filter(Boolean).join(' · ')}
          </ThemedText>
          <View className="flex-row items-center gap-1">
            <ThemedText className="text-xs text-neutral-400">{dueLabel}</ThemedText>
            {charge.hasProof && (
              <>
                <ThemedText className="text-xs text-neutral-400">·</ThemedText>
                <Ionicons className="text-neutral-400" name="attach-outline" size={13} />
                <ThemedText className="text-xs text-neutral-400">Comprovante</ThemedText>
              </>
            )}
          </View>
        </View>

        <View className="shrink-0 flex-row items-center gap-2">
          <View className="items-end gap-1">
            <ThemedText
              className="font-bold text-sm text-foreground"
              style={{ fontVariant: ['tabular-nums'] }}
            >
              {formatCurrency(charge.amount)}
            </ThemedText>
            <StatusBadge status={{ type: 'charge', value: charge.status }} size="xs" />
          </View>
          <Ionicons className="text-neutral-400" name="chevron-forward" size={16} />
        </View>
      </PressableScale>

      {reported && !isAutomatic && (
        <View className="flex-row justify-end">
          <Button
            title={confirming ? 'Confirmando…' : 'Confirmar pagamento'}
            size="sm"
            onPress={onConfirm}
            disabled={confirmDisabled}
          />
        </View>
      )}
    </View>
  );
}
